import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { ArrowUpDown, ChevronDown, Check } from 'lucide-react';

const SORT_OPTIONS = [
  { id: 'default', label: 'Mặc định (KP 01 - KP 45)' },
  { id: 'desc', label: 'Tỷ lệ giảm dần (Cao đến thấp)' },
  { id: 'asc', label: 'Tỷ lệ tăng dần (Thấp đến cao)' }
];

export default function KhuPhoResolutionRateChart({ data, isLoading = false, timeLabel = '' }) {
  const [sortOption, setSortOption] = useState('default');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const chartData = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      return data;
    }
    return [];
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sortedData = useMemo(() => {
    const list = [...chartData];
    if (sortOption === 'desc') {
      return list.sort((a, b) => b.rate - a.rate);
    }
    if (sortOption === 'asc') {
      return list.sort((a, b) => a.rate - b.rate);
    }
    return list;
  }, [chartData, sortOption]);

  const selectedLabel = SORT_OPTIONS.find((opt) => opt.id === sortOption)?.label;

  const chartMinWidth = useMemo(() => {
    return sortedData.length > 15 ? Math.max(sortedData.length * 30, 800) : '100%';
  }, [sortedData.length]);

  return (
    <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200 h-full min-w-0 flex flex-col justify-between overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xs md:text-sm font-bold text-gray-800 tracking-wider uppercase m-0">
            TỶ LỆ XỬ LÝ ĐÚNG HẠN THEO KHU PHỐ (%) {timeLabel ? `(${timeLabel})` : ''}
          </h2>
        </div>

        {/* Custom Dropdown Sort Controls */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <label className="text-xs text-gray-500 font-medium whitespace-nowrap flex items-center gap-1">
            <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
            Sắp xếp:
          </label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="h-8 bg-gray-50 hover:bg-gray-100 border border-gray-200 px-2.5 rounded-md flex items-center gap-1.5 transition-all text-xs font-semibold text-gray-700 cursor-pointer"
            >
              <span>{selectedLabel}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
            </button>

            {isOpen && (
              <div className="absolute right-0 mt-1.5 w-60 bg-white rounded-lg shadow-xl border border-gray-200 p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setSortOption(opt.id);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center justify-between cursor-pointer ${
                      sortOption === opt.id
                        ? 'bg-emerald-50 text-emerald-600 font-bold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{opt.label}</span>
                    {sortOption === opt.id && <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="w-full min-w-0 overflow-x-auto flex-1 flex flex-col justify-center">
        {isLoading ? (
          <div className="w-full h-[310px] flex items-center justify-center animate-pulse bg-gray-50/50 rounded-md">
            <span className="text-xs text-gray-400 font-semibold">Đang tải biểu đồ...</span>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="w-full h-[310px] flex items-center justify-center rounded-md bg-gray-50/50 text-xs font-semibold text-gray-400">
            Chưa có dữ liệu tỷ lệ xử lý theo khu phố
          </div>
        ) : (
          <div
            style={{
              minWidth: typeof chartMinWidth === 'number' ? `${chartMinWidth}px` : chartMinWidth,
              width: '100%'
            }}
            className="h-[310px]"
          >
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={310}>
            <AreaChart
              data={sortedData}
              margin={{ top: 10, right: 15, left: -15, bottom: 35 }}
            >
              <defs>
                <linearGradient id="rateGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                interval={0}
                angle={-45}
                textAnchor="end"
                tick={{ fontSize: 10.5, fill: '#4b5563' }}
                height={35}
                dy={2}
              />
              <YAxis
                ticks={[0, 25, 50, 75, 100]}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                tick={{ fontSize: 10.5, fill: '#4b5563' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ stroke: '#10B981', strokeWidth: 1, strokeDasharray: '3 3' }}
                formatter={(value) => [`${value}%`, 'Tỷ lệ đúng hạn']}
                labelStyle={{ color: '#1f2937', fontWeight: '600' }}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}
              />
              <Area
                type="monotone"
                dataKey="rate"
                stroke="#10B981"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#rateGradient)"
                dot={{ fill: '#10B981', r: 3, strokeWidth: 1.5, stroke: '#ffffff' }}
                activeDot={{ r: 5, fill: '#059669', stroke: '#ffffff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        )}
      </div>
    </div>
  );
}
